import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adsListingService } from '../ads-listing.service';
import type {
	TUseToggleAdStatusConfig,
	TToggleAdStatusParams,
	TUseToggleAdStatusReturn
} from './use-toggle-ad-status.types';

/**
 * Hook para alternar status de um anúncio (ativo/inativo)
 *
 * Invalida o cache da lista após sucesso para recarregar.
 *
 * @example
 * ```tsx
 * const { toggleStatus, isToggling } = useToggleAdStatus({
 *   vehicleType: 'car',
 *   onSuccess: (data) => console.log(`Status alterado para ${data.status}`)
 * });
 *
 * toggleStatus({ id: 12345, status: 'inactive' });
 * ```
 */
const useToggleAdStatus = (config: TUseToggleAdStatusConfig): TUseToggleAdStatusReturn => {
	const { vehicleType, onSuccess, onError } = config;
	const queryClient = useQueryClient();

	const { mutate, mutateAsync, isPending, isError, error } = useMutation({
		mutationFn: ({ id, status }: TToggleAdStatusParams) =>
			adsListingService.toggleAdStatus(vehicleType, id, status),
		onSuccess: (data) => {
			void queryClient.invalidateQueries({
				queryKey: ['ads', 'list', vehicleType],
				exact: false
			});
			void queryClient.invalidateQueries({
				queryKey: ['ads', 'tab-count', vehicleType],
				exact: false
			});
			void queryClient.invalidateQueries({
				queryKey: ['ads', 'detail', vehicleType, data.id],
				exact: false
			});
			onSuccess?.(data);
		},
		onError: (err: Error) => {
			onError?.(err);
		}
	});

	return {
		toggleStatus: mutate,
		toggleStatusAsync: mutateAsync,
		isToggling: isPending,
		isError,
		error: error as Error | null
	};
};

export { useToggleAdStatus };
